/**
 * ElementCalculationEngine
 * 
 * Pure calculation engine for planet and yoga element positioning.
 * Separates coordinate calculations from rendering logic.
 * 
 * Architecture: Universal Layer-Based Architecture v2.0.0
 * Phase: Calculation Layer Implementation
 * 
 * Features:
 * - Pure coordinate calculations
 * - Mode-independent positioning logic
 * - Comprehensive debug traces
 * - Inset system integration
 * - Container bounds handling
 */

import { devLog, devWarn, devError } from '../../../../../utils/devLog';
import { GrahaPosition } from '../../../../../models/types';
import { CoordinateCalculator } from '../modules/CoordinateCalculator';
import { getInsetController } from '../modules/InsetConsoleControllerV2';
import { EngineStatsTracker, DebugTraceManager } from './shared/EngineStatsTracker';

// Debug trace configuration
const DEBUG_TRACES = DebugTraceManager.create({
    CALCULATION_ENGINE: false,
    COORDINATE_CALC: false,
    POSITION_CALC: false,
    INSET_INTEGRATION: false
});

/**
 * Unified element representing any renderable chart element
 */
export interface UnifiedElement {
    id: string;
    type: 'planet' | 'yoga';
    name: string;
    displayName: string;
    position: GrahaPosition;
    containerIndex: number;
    houseNumber: number;
    degree: number;
    special:  'none'| 'lagna' | 'hora'
    // Yoga-specific properties
    planets?: GrahaPosition[];
    planetCount?: number;
    
    // Debug information
    debugInfo?: {
        source: string;
        calculationTime: number;
        originalData: any;
    };
}

/**
 * Element with calculated absolute coordinates
 */
export interface PositionedElement extends UnifiedElement {
    coordinates: {
        x: number;
        y: number;
        side: number; // 0 = inner, 1 = outer
        angle: number;
        lineCoords?: {
            startX: number;
            startY: number;
            endX: number;
            endY: number;
        };
    };
    containerBounds: ContainerBounds;
    
    // Debug traces
    debugTrace?: {
        calculationSteps: string[];
        coordinateSource: string;
        insetRatio: number;
        calculationDuration: number;
    };
}

/**
 * Container bounds with absolute positioning support
 */
export interface ContainerBounds {
    width: number;
    height: number;
    x?: number; // Absolute x position for overlay
    y?: number; // Absolute y position for overlay
    index: number;
    debugInfo?: {
        source: string;
        transformApplied: boolean;
    };
}

/**
 * Pure calculation engine for element positioning
 */
export class ElementCalculationEngine {
    private static instance: ElementCalculationEngine | null = null;
    private coordinateCalculator: CoordinateCalculator;
    
    // Debug state
    private statsTracker = new EngineStatsTracker(
        { totalCalculations: 0, averageTime: 0, lastCalculationTime: 0 },
        'totalCalculations',
        'averageTime'
    );

    private constructor() {
        this.coordinateCalculator = CoordinateCalculator.getInstance();

        if (DEBUG_TRACES.CALCULATION_ENGINE) {
            devLog('🔧 ElementCalculationEngine: Initialized with debug traces enabled');
        }
    }

    public static getInstance(): ElementCalculationEngine {
        if (!ElementCalculationEngine.instance) {
            ElementCalculationEngine.instance = new ElementCalculationEngine();
        }
        return ElementCalculationEngine.instance;
    }

    /**
     * Calculate absolute positions for unified elements
     */
    public calculateElementPositions(
        elements: UnifiedElement[],
        containerBounds: ContainerBounds[],
        mode: 'symbols' | 'short' | 'full'
    ): PositionedElement[] {
        const startTime = performance.now();
        
        if (DEBUG_TRACES.CALCULATION_ENGINE) {
            devLog(`🎯 ElementCalculationEngine: Starting position calculation for ${elements.length} elements in ${mode} mode`);
        }

        const positionedElements: PositionedElement[] = [];
        const currentInsetRatio = getInsetController().getInsetRatio();

        if (DEBUG_TRACES.INSET_INTEGRATION) {
            devLog(`📐 ElementCalculationEngine: Using inset ratio ${currentInsetRatio}`);
        }

        for (const element of elements) {
            const elementStartTime = performance.now();
            const bounds = containerBounds[element.containerIndex];
            
            if (!bounds) {
                devWarn(`⚠️ ElementCalculationEngine: No bounds found for container ${element.containerIndex}`);
                continue;
            }

            const coordinates = this.calculateElementCoordinates(element, bounds, currentInsetRatio);
            
            if (coordinates) {
                const elementDuration = performance.now() - elementStartTime;
                
                const positionedElement: PositionedElement = {
                    ...element,
                    coordinates,
                    containerBounds: bounds,
                    debugTrace: {
                        calculationSteps: [
                            `Container bounds: ${bounds.width}x${bounds.height}`,
                            `Inset ratio: ${currentInsetRatio}`,
                            `House: ${element.houseNumber}, Degree: ${element.degree}`,
                            `Final position: (${coordinates.x.toFixed(2)}, ${coordinates.y.toFixed(2)})`
                        ],
                        coordinateSource: 'CoordinateCalculator',
                        insetRatio: currentInsetRatio,
                        calculationDuration: elementDuration
                    }
                };

                positionedElements.push(positionedElement);

                if (DEBUG_TRACES.POSITION_CALC) {
                    devLog(`📍 Element "${element.name}" positioned at (${coordinates.x.toFixed(2)}, ${coordinates.y.toFixed(2)}) side:${coordinates.side}`);
                }
            }
        }

        const totalDuration = performance.now() - startTime;
        this.updateCalculationStats(totalDuration);

        if (DEBUG_TRACES.CALCULATION_ENGINE) {
            devLog(`✅ ElementCalculationEngine: Completed ${positionedElements.length} calculations in ${totalDuration.toFixed(2)}ms`);
        }

        return positionedElements;
    }

    /**
     * Calculate coordinates for a single element
     */
    private calculateElementCoordinates(
        element: UnifiedElement,
        bounds: ContainerBounds,
        insetRatio: number
    ): PositionedElement['coordinates'] | null {
        try {
            // Calculate degree line coordinates first
            const lineCoords = this.coordinateCalculator.calculateDegreeLineCoordinates(
                element.containerIndex,
                bounds,
                insetRatio
            );

            // Calculate position on the degree line
            const position = this.coordinateCalculator.getPointOnArcPath(
                element.containerIndex,
                element.degree / 30, // Convert degree to progress (0-1)
                bounds,
                insetRatio
            );

            if (!position) {
                devWarn(`⚠️ ElementCalculationEngine: Failed to calculate position for ${element.name}`);
                return null;
            }

            // Apply container offset for overlay positioning
            const finalPosition = {
                x: position.x + (bounds.x || 0),
                y: position.y + (bounds.y || 0)
            };

            const finalLineCoords = lineCoords ? {
                startX: lineCoords.startX + (bounds.x || 0),
                startY: lineCoords.startY + (bounds.y || 0),
                endX: lineCoords.endX + (bounds.x || 0),
                endY: lineCoords.endY + (bounds.y || 0)
            } : undefined;

            if (DEBUG_TRACES.COORDINATE_CALC) {
                devLog(`🧮 Coordinates for ${element.name}: base(${position.x.toFixed(2)}, ${position.y.toFixed(2)}) → final(${finalPosition.x.toFixed(2)}, ${finalPosition.y.toFixed(2)})`);
            }

            return {
                x: finalPosition.x,
                y: finalPosition.y,
                side: 0, // Will be assigned by side distributor
                angle: this.calculateAngleFromDegree(element.degree),
                lineCoords: finalLineCoords
            };

        } catch (error) {
            devError(`❌ ElementCalculationEngine: Error calculating coordinates for ${element.name}:`, error);
            return null;
        }
    }

    /**
     * Calculate angle from degree for positioning
     */
    private calculateAngleFromDegree(degree: number): number {
        // Convert astrological degree to mathematical angle
        // Astrological: 0° = East, 90° = North
        // Mathematical: 0° = East, 90° = North (same in our coordinate system)
        return degree;
    }

    /**
     * Update calculation performance statistics
     */
    private updateCalculationStats(duration: number): void {
        this.statsTracker.update(duration, (stats) => {
            stats.lastCalculationTime = duration;
        });
    }

    /**
     * Get calculation performance statistics
     */
    public getCalculationStats() {
        return this.statsTracker.get();
    }

    /**
     * Reset calculation statistics
     */
    public resetStats(): void {
        this.statsTracker.reset();

        if (DEBUG_TRACES.CALCULATION_ENGINE) {
            devLog('📊 ElementCalculationEngine: Statistics reset');
        }
    }

    /**
     * Enable/disable debug traces
     */
    public static setDebugTraces(enabled: boolean): void {
        DebugTraceManager.setAll(DEBUG_TRACES, enabled);
        devLog(`🔍 ElementCalculationEngine: Debug traces ${enabled ? 'enabled' : 'disabled'}`);
    }
}

// Export for browser console debugging
if (typeof window !== 'undefined') {
    (window as any).ElementCalculationEngine = ElementCalculationEngine;
}